import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { INLINES } from '@contentful/rich-text-types';
import type { Document } from '@contentful/rich-text-types';
import { Col } from '@opendoor/atoms/box';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';

// The inline legal links carry `!text-od-content-inverse-primary` because Atoms
// `Link` hard-codes the brand-accent color (it has no inverse variant); the `!`
// forces them to the footer's content color.
const FooterLegal = ({
  extraLegalText,
  showCustomerTestimonialLegal,
  showEligibilityLegal,
  showPromotionLegal,
  showThePdpCopyrightLegal,
}: {
  extraLegalText?: Document | undefined;
  showCustomerTestimonialLegal?: boolean | undefined;
  showEligibilityLegal?: boolean | undefined;
  showPromotionLegal?: boolean | undefined;
  showThePdpCopyrightLegal?: boolean | undefined;
}) => {
  return (
    <Col className="gap-4 rounded-xl bg-od-bg-inverse-secondary p-4">
      {extraLegalText && (
        <Col className="gap-4">
          {documentToReactComponents(extraLegalText, {
            renderNode: {
              [INLINES.HYPERLINK]: (node, children) => (
                <Link
                  href={node.data.uri}
                  analyticsName="cosmos-footer"
                  className="!text-od-content-inverse-primary"
                >
                  {children}
                </Link>
              ),
            },
          })}
        </Col>
      )}
      {showEligibilityLegal && (
        <Text as="p" className="text-body-md">
          Offer eligibility and purchase price vary.
        </Text>
      )}
      {showCustomerTestimonialLegal && (
        <Text as="p" className="text-body-md">
          Featured customers may have been compensated for their time. Their opinions and views are
          solely their own.
        </Text>
      )}
      {showThePdpCopyrightLegal && (
        <Text as="p" className="text-body-md">
          Data is subject to copyright, and provided solely for personal, not commercial use. All
          property information should be verified by the recipient and none is guaranteed as
          accurate.
        </Text>
      )}
      {showPromotionLegal && (
        <Text as="p" className="text-body-md">
          Eligibility may vary. Not all promotions may be combined.
        </Text>
      )}
      <Text as="p" className="text-body-md">
        CA: Opendoor Brokerage Inc. DRE #02061130
      </Text>
      <Text as="p" className="text-body-md">
        NY:{' '}
        <Link
          analyticsName="cosmos-landing-page-v2-footer-legal-link"
          aria-label="Licensed as Opendoor Brokerage LLC pdf link"
          href="https://odfil.es/static/documents/Dan+NY+Notarized+Doc.pdf"
          className="!text-od-content-inverse-primary"
        >
          Licensed as Opendoor Brokerage LLC
        </Link>
        ,{' '}
        <Link
          analyticsName="cosmos-landing-page-v2-footer-legal-link"
          aria-label="Fair Housing Notice pdf link"
          href="https://odfil.es/static/documents/fair-housing-notice.pdf"
          className="!text-od-content-inverse-primary"
        >
          Fair Housing Notice
        </Link>
      </Text>
      <Text as="p" className="text-body-md">
        TX:{' '}
        <Link
          analyticsName="cosmos-landing-page-v2-footer-legal-link"
          aria-label="TREC Information About Brokerage Services pdf link"
          href="https://odfil.es/static/IABS_Jan_2026.pdf"
          className="!text-od-content-inverse-primary"
        >
          TREC Information About Brokerage Services
        </Link>
        ,{' '}
        <Link
          analyticsName="cosmos-landing-page-v2-footer-legal-link"
          aria-label="TREC Consumer Protection Notice pdf link"
          href="https://www.trec.texas.gov/forms/consumer-protection-notice"
          className="!text-od-content-inverse-primary"
        >
          TREC Consumer Protection Notice
        </Link>
      </Text>
      <Text as="p" className="text-body-md">
        Opendoor Labs Inc. is dedicated to improving the accessibility of its Website and we view
        accessibility as a continually ongoing effort. Please contact us at{' '}
        <Link
          href="mailto:support@opendoor.com"
          aria-label="Contact support link"
          analyticsName="cosmos-contact-opendoor-email"
          className="!text-od-content-inverse-primary"
        >
          support@opendoor.com
        </Link>{' '}
        with any support needs, feedback or suggestions.
      </Text>
    </Col>
  );
};

export default FooterLegal;
